// Operator-side voicemail panel — the SubscriberDetail "Voicemail" tab body,
// plus the VoicemailSettings form shared with ExtensionDetail.
//
// PRIVACY RULE: voicemail CONTENT (audio + transcripts) is subscriber-only —
// staff see METADATA (caller, time, duration, counts) and manage settings.
// There is no play/download affordance here on purpose; the API has no staff
// audio route either. Support listens via portal view-as (audited) if the
// subscriber asks for help.
//
// Management mode (per subscriber): 'freepbx' = the PBX owns voicemail email
// settings (edit them per extension below); 'cloud' = Verso Cloud owns the
// features — new messages are transcribed and emailed (transcript + WAV) to
// the subscriber's email on file, and the PBX-side email settings are cleared
// at flip time so nothing is sent twice.

import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api.js';
import FeatureSwitch from './FeatureSwitch.jsx';
import { useVoicemailStatus } from '../../hooks/useVoicemailStatus.js';

const GREETING_LABELS = {
  unavail: 'Unavailable',
  busy:    'Busy',
  temp:    'Temporary',
  greet:   'Greeting',
};
const GREETING_ORDER = ['unavail', 'busy', 'temp', 'greet'];

function fmtDuration(seconds) {
  if (seconds == null || seconds < 0) return '—';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

function fmtWhen(epoch) {
  if (!epoch) return '—';
  return new Date(Number(epoch) * 1000).toLocaleString([], {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

function StatCard({ label, value, sub }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
      <div className="text-xs font-medium uppercase tracking-wider text-gray-500">{label}</div>
      <div className="mt-1 text-xl font-semibold text-gray-900">{value}</div>
      {sub && <div className="text-xs text-gray-400">{sub}</div>}
    </div>
  );
}

// Editable voicemail settings — the astdb-backed subset the agent can write
// (email, attach, sayCallerId, envelope, deleteAfterEmail). Enable/disable
// and PIN live in voicemail.conf and need the agent's config-reload path
// (Phase 2.5b) before they're settable here. Shared by ExtensionDetail's
// Voicemail tab and the freepbx-mode config section below. Only meaningful
// while the subscriber's voicemail_mode is 'freepbx'.
export function VoicemailSettings({ vm, agentId, ext, canWrite, onSaved }) {
  const [form, setForm] = useState({
    email: vm.email || '',
    attach: Boolean(vm.attach),
    sayCallerId: Boolean(vm.sayCallerId),
    envelope: Boolean(vm.envelope),
    deleteAfterEmail: Boolean(vm.deleteAfterEmail),
  });
  useEffect(() => {
    setForm({
      email: vm.email || '',
      attach: Boolean(vm.attach),
      sayCallerId: Boolean(vm.sayCallerId),
      envelope: Boolean(vm.envelope),
      deleteAfterEmail: Boolean(vm.deleteAfterEmail),
    });
  }, [vm]);

  const save = useMutation({
    mutationFn: (body) => api.put(`/agents/${agentId}/proxy/subscribers/${encodeURIComponent(ext)}/voicemail`, body),
    onSuccess: () => onSaved?.(),
  });

  const dirty =
    form.email !== (vm.email || '') ||
    form.attach !== Boolean(vm.attach) ||
    form.sayCallerId !== Boolean(vm.sayCallerId) ||
    form.envelope !== Boolean(vm.envelope) ||
    form.deleteAfterEmail !== Boolean(vm.deleteAfterEmail);

  const Toggle = ({ field, label, hint }) => (
    <label className="flex items-start gap-2 text-sm text-gray-700">
      <input
        type="checkbox"
        checked={form[field]}
        disabled={!canWrite}
        onChange={(e) => setForm((f) => ({ ...f, [field]: e.target.checked }))}
        className="mt-0.5"
      />
      <span>
        {label}
        {hint && <span className="block text-xs text-gray-400">{hint}</span>}
      </span>
    </label>
  );

  return (
    <div className="mt-4 max-w-lg space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700">Notification email</label>
        <input
          type="email"
          value={form.email}
          disabled={!canWrite}
          onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
          placeholder="voicemail-to-email address"
          className="mt-1 w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-500"
        />
      </div>
      <div className="space-y-2">
        <Toggle field="attach"           label="Attach message audio to email" />
        <Toggle field="sayCallerId"      label="Say caller ID before playback" />
        <Toggle field="envelope"         label="Play envelope (date/time) before playback" />
        <Toggle field="deleteAfterEmail" label="Delete message after emailing" hint="Only takes effect when attach is on." />
      </div>
      <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
        Mailbox enable/disable and PIN changes aren&rsquo;t supported from here yet —
        they live in the PBX voicemail config and need an agent update (tracked).
        {' '}Building a line sets the mailbox PIN to the extension, so the subscriber is
        prompted to choose their own the first time they dial in.
        {vm.pinNote ? ` Agent note: ${vm.pinNote}` : ''}
      </div>
      {save.isError && (
        <p className="text-sm text-red-600">{save.error?.body?.error || save.error?.message || 'Save failed'}</p>
      )}
      {canWrite && (
        <button
          onClick={() => save.mutate(form)}
          disabled={!dirty || save.isPending}
          className="rounded-md bg-blue-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
        >
          {save.isPending ? 'Saving…' : 'Save voicemail settings'}
        </button>
      )}
    </div>
  );
}

// Per-extension live config block. Own component so each extension gets its
// own useQuery (hooks can't live in a loop in the parent). `bare` skips the
// card wrapper + extension header when the parent (a VoicemailBox) already
// provides the extension identity.
function ExtensionVoicemailConfig({ agentId, ext, canWrite, bare = false }) {
  const queryClient = useQueryClient();
  const vmQ = useQuery({
    queryKey: ['ext-vm-config', agentId, ext],
    queryFn:  () => api.get(`/agents/${agentId}/proxy/subscribers/${encodeURIComponent(ext)}/voicemail`),
    enabled:  Boolean(agentId && ext),
    retry: 1,
  });
  const body = (
    <>
      {vmQ.data?.enabled != null && (
        <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
          vmQ.data.enabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
        }`}>
          {vmQ.data.enabled ? 'Voicemail on' : 'Voicemail off'}
        </span>
      )}
      {vmQ.isLoading && <p className="mt-3 text-sm text-gray-400">Loading config…</p>}
      {vmQ.isError && (
        <p className="mt-3 text-sm text-gray-400">
          Couldn&rsquo;t read voicemail config from the PBX right now.
        </p>
      )}
      {vmQ.data && (
        <VoicemailSettings
          vm={vmQ.data}
          agentId={agentId}
          ext={ext}
          canWrite={canWrite}
          onSaved={() => queryClient.invalidateQueries({ queryKey: ['ext-vm-config', agentId, ext] })}
        />
      )}
    </>
  );
  if (bare) return <div>{body}</div>;
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <div className="flex items-center gap-2">
        <span className="font-mono text-sm font-medium text-gray-900">{ext}</span>
      </div>
      {body}
    </div>
  );
}

// Per-mailbox CLOUD settings (cloud mode): whether Verso keeps messages, the
// transcription toggle, and who receives THIS extension's voicemail by email.
// Merged server-side into settings_json.voicemail.
//
// The recipient list is authoritative for this extension, not an "extras" list:
// an operator can take someone off one mailbox who is on for the account
// generally. Until the mailbox is given a list it follows the account default,
// so changing someone's account-wide switch still moves every mailbox nobody has
// customised. Names come from the account's users and are never typed — a
// recording is the customer's, so receiving it is a membership decision, and a
// free-text address was invisible on the Account users page where an operator
// goes to answer "who gets voicemail email?".
// EXPORTED 2026-08-17. These settings belong to the LINE, not to a row in a message
// list — Kevin: "the voicemail status should be per line, so the settings should be in
// the top, not on each extension. In the case of a ring group, there is only one
// mailbox and it auto selects the primary extension." The line tab now renders this in
// its header card; the mailbox below is messages and nothing else.
export function MailboxCloudSettings({ box, subscriberId, canWrite, onSaved, transcriptionAllowed = true, tierName, pbxEnabled = null }) {
  const vs = box.voicemail_settings || { enabled: true, transcribe: true, recipient_user_ids: null };
  // What this box should show BEFORE anyone has set the cloud flag.
  //
  // A new extension is provisioned with vmEnable:false on the PBX and no
  // `voicemail` key in Verso, and the cloud flag reads absent-as-on (the ingest
  // gate needs that, so legacy extensions keep receiving). The result was a
  // ticked "Voicemail enabled" over an extension with no mailbox — and because it
  // was already ticked, there was nothing to click. So when nobody has configured
  // it, believe the PBX: that's where the mailbox either exists or doesn't.
  const neverConfigured = vs.configured === false;
  const initialEnabled = neverConfigured && pbxEnabled !== null
    ? pbxEnabled
    : vs.enabled !== false;
  // null = following the account default; an array (empty included) = its own list.
  const savedIds = useMemo(
    () => (Array.isArray(vs.recipient_user_ids)
      ? vs.recipient_user_ids.map(Number).sort((a, b) => a - b)
      : null),
    [vs.recipient_user_ids],
  );
  const [enabled, setEnabled] = useState(initialEnabled);
  const [transcribe, setTranscribe] = useState(vs.transcribe !== false);
  // The list this mailbox sends to. Seeded from the account default until somebody touches a
  // switch, at which point it becomes the mailbox's own — `setCustom` records that so the effect
  // below does not re-seed it back to the default on the next render.
  const [picked, setPicked] = useState(null);
  const [, setCustom] = useState(savedIds !== null);

  // Same query key as AccountUsersPanel / VoicemailRecipientsModal, so the roster
  // is shared rather than fetched three times.
  const usersQ = useQuery({
    queryKey: ['subscriber', String(subscriberId), 'users'],
    queryFn: () => api.get(`/subscribers/${subscriberId}/users`),
    enabled: Boolean(subscriberId),
  });
  // useMemo so the seeding effect below doesn't see a new array identity every render.
  const users = useMemo(() => usersQ.data?.users || [], [usersQ.data]);

  // The account default, as ids — what the boxes are seeded with while this
  // mailbox has no list of its own.
  const defaultIds = useMemo(
    () => users.filter((u) => u.voicemailNotify && u.status === 'active').map((u) => u.id).sort((a, b) => a - b),
    [users],
  );


  // AUTO-COMMIT (2026-08-19). Kevin: "the rest of the copy and the save can go away." Each switch
  // sends the whole settings object with its one change applied, so there is no Save step to forget
  // and no half-saved state to reason about. `pendingField` is which switch is in flight, so only
  // that switch shows busy rather than the panel greying out under the operator's hand.
  const [pendingField, setPendingField] = useState(null);
  // Absent reads as ON, matching every other flag in this blob: a mailbox nobody has configured keeps
  // behaving as it did before the switch existed.
  const [emailEnabled, setEmailEnabled] = useState(vs.emailEnabled !== false);
  // Re-sync every switch when the saved settings change under us (another tab, a fan-out
  // write). Must sit below the useState calls it writes to.
  useEffect(() => {
    setEnabled(initialEnabled);
    setTranscribe(vs.transcribe !== false);
    setEmailEnabled(vs.emailEnabled !== false);
    setCustom(savedIds !== null);
    setPicked(savedIds ?? defaultIds);
  }, [initialEnabled, vs.transcribe, vs.emailEnabled, savedIds, defaultIds]);
  const save = useMutation({
    mutationFn: (patch) => api.put(`/sip-accounts/${box.sip_account_id}/voicemail-settings`, {
      enabled,
      transcribe,
      // An explicit array from here on: touching any switch makes this the mailbox's own list,
      // which is what an operator means by touching it.
      recipient_user_ids: picked || [],
      email_enabled: emailEnabled,
      ...patch,
    }),
    onSettled: () => setPendingField(null),
    onSuccess: () => onSaved?.(),
  });
  // KEEPING THE PBX COPY, OR NOT — Mode A.
  //
  // Kevin, 2026-08-22: "how does a subscriber set the voicemail to be deleted from pbx after copy
  // to the cloud". Nobody could. The mode was only ever INHERITED from FreePBX's delete-after-email
  // flag, and moving a mailbox to cloud voicemail clears that flag (its post-email unlink races the
  // agent's spool ingest), so after the flip there was no way to say it again — and nothing read it
  // even when it had been captured.
  //
  // Separate route and separate mutation from the switches above: this is not a voicemail_settings
  // field, it is a column the retention sweep also writes. `null` means nobody has said and nothing
  // was inherited, which is NOT the same as "keep" and must not render as an answered question.
  const retention = box.pbx_retention ?? null;
  const setRetention = useMutation({
    mutationFn: (next) => api.put(`/voicemails/retention/${box.sip_account_id}`, { retention: next }),
    onSettled: () => setPendingField(null),
    onSuccess: () => onSaved?.(),
  });

  const commit = (patch, field) => {
    setPendingField(field);
    // Optimistic so the switch moves under the finger; the refetch in onSaved is the authority.
    if ('transcribe' in patch) setTranscribe(patch.transcribe);
    if ('email_enabled' in patch) setEmailEnabled(patch.email_enabled);
    if ('recipient_user_ids' in patch) { setCustom(true); setPicked(patch.recipient_user_ids); }
    save.mutate(patch);
  };

  return (
    <div className="max-w-lg space-y-3">
      {/* THE "VOICEMAIL ENABLED" CHECKBOX IS GONE FROM HERE (2026-08-19).
          Kevin: "when switching the voicemail to the cloud, there is a second enable checkbox
          that is now redundant."

          It was the master per-extension toggle, writing the same `enabled` on the same
          `voicemail-settings` route as the line's new switch. Two controls for one field, one of
          them buried behind a "Cloud settings" disclosure — and a per-extension place for what is
          a per-line decision (the mailbox belongs to the primary extension; the ring group's
          no-answer destination points there). The line header owns it now.

          WHAT STAYS IS THE DIAGNOSIS. These sentences name states an operator cannot otherwise
          tell apart, and none of them is a control — a mailbox nobody has ever set up looks
          exactly like one deliberately switched off, and Verso disagreeing with the PBX looks like
          nothing at all until it is said. */}
      {(neverConfigured || (pbxEnabled !== null && pbxEnabled !== (vs.enabled !== false))) && (
        <div className="text-sm text-gray-700">
          {neverConfigured && pbxEnabled === false && (
            <span className="block text-xs text-amber-700">
              No mailbox on the PBX yet — new extensions are created without one. Turn voicemail on
              above to add it.
            </span>
          )}
          {neverConfigured && pbxEnabled === null && (
            <span className="block text-xs text-gray-400">
              Never set for this extension; the PBX could not be reached to check whether it has a
              mailbox.
            </span>
          )}
          {!neverConfigured && pbxEnabled !== null && pbxEnabled !== (vs.enabled !== false) && (
            <span className="block text-xs text-red-700">
              Verso says {vs.enabled !== false ? 'on' : 'off'} but the PBX says{' '}
              {pbxEnabled ? 'on' : 'off'}. Use the switch above to push Verso’s setting to the PBX.
            </span>
          )}
        </div>
      )}
      {/* SLIDERS, AND THEY SAVE THEMSELVES.
          Kevin, 2026-08-19: "instead of the button, just list the users on the account that can
          receive emails and add a slider. the rest of the copy and the save can go away. transcribe
          can also be a slider."

          WHAT WAS HERE: a transcribe checkbox with a hint, a "Who gets voicemail for this extension"
          heading, a "Use the account default" link, a paragraph explaining custom versus default, a
          checkbox per person each carrying two lines of status copy, a red warning, and a Save
          button. Nine things to read to answer "who gets this customer's voicemail". */}
      <div className={enabled ? 'space-y-3' : 'pointer-events-none space-y-3 opacity-40'}>
        <div className="flex items-center justify-between gap-3">
          <span className={`text-sm ${transcriptionAllowed ? 'text-gray-700' : 'text-gray-400'}`}>
            Transcribe new messages
          </span>
          <FeatureSwitch
            state={transcriptionAllowed ? (transcribe ? 'on' : 'off') : 'locked'}
            label="Transcribe new messages"
            canWrite={canWrite}
            busy={save.isPending && pendingField === 'transcribe'}
            reason={transcriptionAllowed ? null
              : `Not included in this service tier${tierName ? ` (${tierName})` : ''}.`}
            onChange={(next) => commit({ transcribe: next }, 'transcribe')}
          />
        </div>

        <div className="flex items-center justify-between gap-3">
          <span className="text-sm text-gray-700">
            Email voicemails
            {/* The one clause worth keeping from the block this replaced: what still happens when it
                is off. Without it, "off" reads as "the message is lost". */}
            <span className="block text-xs text-gray-400">
              Off = messages are still recorded and readable in the portal, just not emailed.
            </span>
          </span>
          <FeatureSwitch
            state={emailEnabled ? 'on' : 'off'}
            label="Email voicemails for this line"
            canWrite={canWrite}
            busy={save.isPending && pendingField === 'email'}
            onChange={(next) => commit({ email_enabled: next }, 'email')}
          />
        </div>

        <div className="flex items-center justify-between gap-3">
          <span className="text-sm text-gray-700">
            Delete from the phone system once copied
            <span className="block text-xs text-gray-400">
              {retention === null
                // Said plainly rather than shown as an off switch. The pre-cloud flag was cleared
                // by the move to cloud voicemail, so for most mailboxes this is genuinely unknown.
                ? 'Not set — the PBX keeps its copy until someone chooses.'
                : retention === 'delete'
                  ? 'Verso is the only store. The PBX copy is removed after the cloud copy is saved.'
                  : 'Both stores keep a copy.'}
            </span>
          </span>
          <FeatureSwitch
            state={retention === 'delete' ? 'on' : 'off'}
            label="Delete from the phone system once copied to the cloud"
            canWrite={canWrite}
            busy={setRetention.isPending && pendingField === 'retention'}
            onChange={(next) => {
              setPendingField('retention');
              setRetention.mutate(next ? 'delete' : 'keep');
            }}
          />
        </div>
        {setRetention.isError && (
          <p className="text-xs text-red-600">
            {setRetention.error?.body?.error || setRetention.error?.message || 'could not change it'}
          </p>
        )}

        <div className={emailEnabled ? '' : 'pointer-events-none opacity-40'}>
          <div className="text-sm font-medium text-gray-700">Voicemail email goes to</div>
          {usersQ.isLoading && <p className="mt-1 text-xs text-gray-400">Loading the account&rsquo;s people…</p>}

          {!usersQ.isLoading && users.length === 0 && (
            <p className="mt-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
              Nobody is on this account yet. Add someone under Account users first — only people on
              the account can receive a customer&rsquo;s voicemail.
            </p>
          )}

          {users.length > 0 && picked && (
            <ul className="mt-2 divide-y divide-gray-100 rounded-md border border-gray-200 bg-white">
              {users.map((u) => {
                const off = u.status !== 'active';
                return (
                  <li key={u.id} className={`flex items-center justify-between gap-3 px-3 py-2 ${off ? 'bg-gray-50' : ''}`}>
                    <span className="min-w-0 text-xs">
                      <span className={`block truncate ${off ? 'text-gray-400' : 'text-gray-900'}`}>
                        {u.name || u.email}
                        {u.isPrimary && <span className="ml-1.5 text-gray-500">main contact</span>}
                      </span>
                      {u.name && <span className={`block truncate ${off ? 'text-gray-400' : 'text-gray-500'}`}>{u.email}</span>}
                    </span>
                    <FeatureSwitch
                      state={off ? 'locked' : (picked.includes(u.id) ? 'on' : 'off')}
                      label={`Voicemail email to ${u.name || u.email}`}
                      canWrite={canWrite}
                      showState={false}
                      busy={save.isPending && pendingField === `rcpt-${u.id}`}
                      reason={off ? 'Switched off on the account, so nothing is sent to them.' : null}
                      onChange={(next) => {
                        const list = next
                          ? [...new Set([...(picked || []), u.id])].sort((a, b) => a - b)
                          : (picked || []).filter((x) => x !== u.id);
                        commit({ recipient_user_ids: list }, `rcpt-${u.id}`);
                      }}
                    />
                  </li>
                );
              })}
            </ul>
          )}

          {/* The one sentence kept: nobody receiving is a legitimate choice and a dangerous one, and
              it is the only thing here an operator would otherwise have to work out. */}
          {users.length > 0 && picked && picked.length === 0 && (
            <p className="mt-2 text-xs text-red-700">
              Nobody is emailed. Messages are still recorded and visible in the customer&rsquo;s portal.
            </p>
          )}
        </div>
      </div>
      {/* No Save button — every switch above writes on the spot. The error still has to land
          somewhere: a toggle that silently failed reads exactly like one that worked. */}
      {save.isError && (
        <p className="text-sm text-red-600">
          {save.error?.body?.error || save.error?.message || 'That change could not be saved.'}
        </p>
      )}

    </div>
  );
}

// One voicemail BOX — an extension's message metadata + greeting/name
// presence, plus its config: live PBX settings in freepbx mode, cloud
// settings (transcription + email override) in cloud mode. No playback:
// content is subscriber-only.
export function VoicemailBox({ box, subscriberId, agentId, canWrite, showConfigControls, cloudMode, onSettingsSaved, transcriptionAllowed, tierName, pbxEnabled = null }) {
  const [showConfig, setShowConfig] = useState(false);
  const messages = box.messages || [];
  const greetings = useMemo(() => [...(box.greetings || [])].sort(
    (a, b) => GREETING_ORDER.indexOf(a.greeting_type) - GREETING_ORDER.indexOf(b.greeting_type),
  ), [box.greetings]);
  const s = box.summary || {};
  const linked = box.sip_account_id != null;

  return (
    <div className="rounded-lg border border-gray-200 bg-white">
      <div className="flex flex-wrap items-center gap-3 border-b border-gray-200 px-4 py-3">
        <span className="font-mono text-sm font-semibold text-gray-900">{box.extension}</span>
        {!linked && (
          <span className="inline-flex rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700"
                title="This mailbox doesn't match any of the subscriber's current extensions">
            not linked to an extension
          </span>
        )}
        {linked && box.extension_status && box.extension_status !== 'active' && (
          <span className="inline-flex rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
            {box.extension_status}
          </span>
        )}
        {/* MESSAGES, AND WHETHER ANY ARE NEW. The greeting count and "name recorded / —"
            were here too, which made the line an inventory of mailbox internals before it
            said the one thing anybody scans for. Greetings are still listed at the foot of
            the card, where they are a troubleshooting answer rather than a header stat
            (Kevin, 2026-08-13: "dumb this down and keep it simple"). */}
        <span className="text-xs text-gray-500">
          {s.messages ?? 0} message{(s.messages ?? 0) === 1 ? '' : 's'}
          {s.new > 0 && <span className="ml-1 font-medium text-amber-700">({s.new} new)</span>}
        </span>
        <span className="flex-1" />
        {s.last_received && (
          <span className="text-xs text-gray-400">last received {fmtWhen(s.last_received)}</span>
        )}
        {((showConfigControls && agentId) || cloudMode) && linked && (
          <button
            onClick={() => setShowConfig((v) => !v)}
            className={`rounded-md border px-2.5 py-1 text-xs font-medium ${
              showConfig ? 'border-blue-400 bg-blue-50 text-blue-700'
                         : 'border-gray-300 text-gray-600 hover:bg-gray-50'
            }`}
          >
            {showConfig ? 'Hide settings' : (cloudMode ? 'Cloud settings' : 'PBX config')}
          </button>
        )}
      </div>

      {showConfig && linked && (
        <div className="border-b border-gray-200 bg-gray-50/50 px-4 py-3">
          {cloudMode
            ? <MailboxCloudSettings box={box} subscriberId={subscriberId} canWrite={canWrite} transcriptionAllowed={transcriptionAllowed} tierName={tierName} pbxEnabled={pbxEnabled} onSaved={() => { setShowConfig(false); onSettingsSaved?.(); }} />
            : (agentId
              ? <ExtensionVoicemailConfig agentId={agentId} ext={box.extension} canWrite={canWrite} bare />
              : <p className="text-sm text-gray-400">No PBX host resolved for this extension.</p>)}
        </div>
      )}

      {/* Message METADATA — caller/time/duration. No content access. */}
      {messages.length === 0 ? (
        <div className="px-4 py-4 text-sm text-gray-400">No stored messages for this mailbox.</div>
      ) : (
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left text-xs uppercase tracking-wider text-gray-500">
            <tr>
              <th className="px-4 py-2 font-medium">Folder</th>
              <th className="px-4 py-2 font-medium">From</th>
              <th className="px-4 py-2 font-medium">When</th>
              <th className="px-4 py-2 font-medium">Duration</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {messages.map((m) => {
              const isNew = (m.folder || 'INBOX') === 'INBOX';
              return (
                <tr key={m.id} className={isNew ? 'bg-amber-50/40' : ''}>
                  <td className="px-4 py-2">
                    {/* Real folder name — "new" is derived state within the
                        mailbox, not a folder, so INBOX is labeled as itself. */}
                    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                      isNew ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {isNew ? 'INBOX · new' : (m.folder || 'Saved')}
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-4 py-2 font-mono text-xs">{m.callerid || m.from_number || '—'}</td>
                  <td className="whitespace-nowrap px-4 py-2 text-gray-600">{fmtWhen(m.origtime)}</td>
                  <td className="whitespace-nowrap px-4 py-2 font-mono text-xs">{fmtDuration(m.duration)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {greetings.length > 0 && (
        <div className="border-t border-gray-200 px-4 py-2 text-xs text-gray-500">
          Greetings on file: {greetings.map((g) => GREETING_LABELS[g.greeting_type] || g.greeting_type).join(', ')}
        </div>
      )}
    </div>
  );
}

// The freepbx ↔ cloud management-mode switch. org_admin only; flipping to
// cloud clears the PBX email settings for every extension (reported below).
// `compact` renders ONLY the badge and the switch-mode button, for sitting beside the line's
// on/off toggle — Kevin, 2026-08-19: "move the badge and button for switching voicemail to the
// cloud to be next to the on/off toggle." `hideControls` is its other half: the copy below keeps
// explaining who handles voicemail and that the choice is account-wide, without a second badge and
// a second button repeating what is now in the header.
export function VoicemailModeCard({ subscriberId, mode, recipients, canWrite, onChanged, cloudAllowed = true, tierName, scopeNote = null, bare = false, compact = false, hideControls = false }) {
  // Changing delivery used to mean leaving this tab for Account users on the
  // Overview tab. Voicemail delivery is a voicemail decision; it belongs here.
  const [confirming, setConfirming] = useState(null); // target mode or null
  const [result, setResult] = useState(null);
  const flip = useMutation({
    mutationFn: (m) => api.put(`/subscribers/${subscriberId}/voicemail-mode`, { mode: m }),
    onSuccess: (data) => { setConfirming(null); setResult(data); onChanged?.(); },
  });

  const isCloud = mode === 'cloud';
  // `bare` drops the card chrome so this can sit INSIDE the line's voicemail card rather
  // than as a second card beside it (Kevin, 2026-08-17: "consolidate the two top cards").
  // Still a div either way — only the class changes.
  return (
    <div className={compact ? '' : (bare ? '' : 'rounded-lg border border-gray-200 bg-white p-4')}>
      <div className={compact ? 'flex items-center gap-2' : 'flex flex-wrap items-center gap-3'}>
        {!compact && (
        <div className="flex-1">
          <div className="text-sm font-semibold text-gray-900">Voicemail management</div>
          {/* Said out loud because this card now renders on EVERY line's voicemail
              tab (moved there 2026-08-15). Whether Verso or the PBX owns voicemail
              is one decision for the account — an operator looking at one line has
              no other way to know that changing it here changes it everywhere. */}
          {scopeNote && (
            <div className="mt-0.5 text-xs font-medium text-amber-700">{scopeNote}</div>
          )}
          <div className="mt-0.5 text-xs text-gray-500">
            {isCloud
              ? <>Handled by <span className="font-medium text-gray-700">Verso Cloud</span>. Messages are emailed to{' '}
                  {recipients?.length
                    ? <>{recipients.length === 1
                        ? <span className="font-mono">{recipients[0].email}</span>
                        : <>everyone on the account with voicemail email on:{' '}
                            {recipients.map((r, i) => (
                              <span key={r.email}>
                                {i > 0 && ', '}
                                <span className="font-mono">{r.email}</span>
                              </span>
                            ))}
                          </>}
                      </>
                    : <span className="text-red-700">nobody — no one on this account has voicemail email switched on</span>}
                  {/* The per-line list above overrides this; the double-send caveat is
                      our plumbing, not the operator's decision, and it was three of the
                      four clauses in this paragraph. */}
                  .</>
              : <>Managed on the <span className="font-medium text-gray-700">PBX</span> — voicemail-to-email uses the per-extension PBX settings below.</>}
          </div>
        </div>
        )}
        {!hideControls && (
        <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${
          isCloud ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'
        }`}>
          {isCloud ? 'Cloud' : 'PBX'}
        </span>
        )}
        {canWrite && !hideControls && (
          <button
            onClick={() => { setResult(null); setConfirming(isCloud ? 'freepbx' : 'cloud'); }}
            disabled={!isCloud && !cloudAllowed}
            title={!isCloud && !cloudAllowed
              ? `Cloud voicemail is not included in this subscriber's service tier${tierName ? ` (${tierName})` : ''}`
              : undefined}
            className="rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
          >
            Switch to {isCloud ? 'PBX' : 'Cloud'}
          </button>
        )}
      </div>

      {confirming && (
        <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          {confirming === 'cloud' ? (
            <>Switching to <b>Cloud</b> turns off the PBX&rsquo;s own voicemail email on every one of
            this subscriber&rsquo;s extensions — the address, the audio attachment, and
            delete-after-email — and the cloud takes over emailing with transcripts. Messages keep
            recording on the PBX so they can be ingested; nothing is deleted. Continue?</>
          ) : (
            <>Switching to <b>PBX</b> stops cloud emailing immediately. PBX-side email settings
            are NOT restored automatically — reconfigure them per extension below. Continue?</>
          )}
          <div className="mt-2 flex gap-2">
            <button
              onClick={() => flip.mutate(confirming)}
              disabled={flip.isPending}
              className="rounded-md bg-amber-600 px-3 py-1 text-xs font-medium text-white hover:bg-amber-700 disabled:opacity-50"
            >
              {flip.isPending ? 'Switching…' : `Yes, switch to ${confirming === 'cloud' ? 'Cloud' : 'PBX'}`}
            </button>
            <button
              onClick={() => setConfirming(null)}
              className="rounded-md border border-gray-300 px-3 py-1 text-xs text-gray-700 hover:bg-white"
            >
              Cancel
            </button>
          </div>
          {flip.isError && (
            <p className="mt-2 text-xs text-red-600">{flip.error?.body?.error || flip.error?.message || 'Switch failed'}</p>
          )}
        </div>
      )}

      {/* WHAT HAPPENED, IN ONE LINE — plus anything that actually needs a person.
          This printed a row per extension with a ✓/✕ and, on failure, the agent's raw
          error: "Agent 9082a8ae-… PUT /provision/extension/13038236828/voicemail → 400
          extension has no voicemail mailbox — send \"enabled\": true to create one…".
          An operator was being shown an HTTP call and a JSON field name (Kevin,
          2026-08-17). Worse, the commonest cause — no mailbox — is a normal account with
          nothing to switch off, so a working customer read as a failure.

          Now: the count, and only the extensions that genuinely need attention. */}
      {result?.freepbx_email_cleared?.length > 0 && (() => {
        const rows = result.freepbx_email_cleared.filter((r) => r.extension);
        const failed = rows.filter((r) => !r.ok);
        const done = rows.filter((r) => r.ok && !r.skipped).length;
        return (
          <div className="mt-3 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-600">
            <div className="font-medium text-gray-700">
              {done > 0
                ? `PBX voicemail email switched off on ${done} extension${done === 1 ? '' : 's'}.`
                : 'Nothing to switch off on the PBX.'}
            </div>
            {failed.map((r) => (
              <div key={r.extension} className="mt-1 text-amber-800">
                {/* The one case a person can act on keeps its instruction; everything else
                    becomes a plain sentence rather than a transport error. */}
                {r.action_required
                  || `Could not switch off PBX voicemail email for ${r.extension}. Its mailbox `
                     + 'still emails as well, so messages may arrive twice until it is changed '
                     + 'on the PBX.'}
              </div>
            ))}
          </div>
        );
      })()}

      {/* NO "CHOOSE WHO GETS VOICEMAIL EMAIL…" BUTTON. Kevin, 2026-08-19: "there are still two
          buttons for choosing who gets voicemail, there should be 0. just the rows of users."

          It opened a modal onto the account-wide recipient list while the mailbox settings above
          already list the same people with a switch each. It rendered TWICE once this card started
          appearing in two places — compact in the line header and in full below — which is the
          shape of the problem: a control duplicated is a control that will disagree with itself.
          The rows are the answer, in place, with no click to reach them. */}
    </div>
  );
}

/**
 * The SubscriberDetail "Voicemail" tab body — metadata + settings only.
 *
 * Props:
 *   subscriberId — subscribers.id
 *   sipAccounts  — the subscriber's sip_accounts rows (from /sip-accounts)
 *   canWrite     — org_admin (mode flips + config edits, both audited)
 */
export default function SubscriberVoicemailPanel({ subscriberId, sipAccounts = [], canWrite, features = null, tierName = null }) {
  // features === null → untiered/unknown → unrestricted (legacy).
  const cloudAllowed = !features || features.cloud_voicemail !== false;
  const transcriptionAllowed = !features || features.transcription !== 'none';
  const queryClient = useQueryClient();
  const vmQ = useQuery({
    queryKey: ['subscriber', String(subscriberId), 'voicemails'],
    queryFn:  () => api.get(`/subscribers/${subscriberId}/voicemails`),
    refetchInterval: 60_000,
  });

  const serversQ = useQuery({
    queryKey: ['sip-servers'],
    queryFn:  () => api.get('/sip-servers'),
    staleTime: 5 * 60_000,
  });

  // Whether each extension actually HAS a mailbox, read from the PBX. Verso's
  // own flag can't answer that for an extension nobody has configured — absent
  // reads as on, because the ingest gate needs it to. The shared hook means this
  // is the same fetch the badges already use, not a fourth one. `enabled: null`
  // there means the host couldn't be reached, which must stay distinct from off.
  const { entryFor: vmEntryFor } = useVoicemailStatus();
  const agentOf = useMemo(() => {
    const list = Array.isArray(serversQ.data) ? serversQ.data
      : (serversQ.data?.sip_servers || serversQ.data?.servers || []);
    const map = {};
    for (const s of list) map[s.id] = s.agentId || s.agent_id;
    return map;
  }, [serversQ.data]);
  const serverOfAccount = useMemo(() => {
    const map = {};
    for (const a of sipAccounts) map[a.id] = a.sipServerId ?? a.sip_server_id;
    return map;
  }, [sipAccounts]);

  const summary = vmQ.data?.summary;
  const mode = vmQ.data?.voicemail_mode || 'freepbx';
  const boxes = vmQ.data?.boxes || [];
  const unlinked = vmQ.data?.unlinked || [];

  return (
    <div className="space-y-6">
      {/* Metrics strip */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatCard label="Messages" value={summary ? summary.messages : '—'}
                  sub={summary ? `${summary.new} new · ${summary.saved} saved` : null} />
        <StatCard label="Mailboxes" value={vmQ.data ? boxes.length + unlinked.length : '—'}
                  sub={unlinked.length > 0 ? `${unlinked.length} not linked` : null} />
        <StatCard label="Greetings" value={summary ? summary.greetings : '—'} />
        <StatCard label="Last received" value={summary?.last_received ? fmtWhen(summary.last_received) : '—'} />
      </div>

      {/* Where voicemail management lives */}
      {vmQ.data && (
        <VoicemailModeCard
          subscriberId={subscriberId}
          mode={mode}
          recipients={vmQ.data.recipients}
          canWrite={canWrite}
          cloudAllowed={cloudAllowed}
          tierName={tierName}
          onChanged={() => queryClient.invalidateQueries({ queryKey: ['subscriber', String(subscriberId), 'voicemails'] })}
        />
      )}

      {/* Privacy note — content is subscriber-only by design. */}
      <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-500">
        🔒 Voicemail audio and transcripts are private to the subscriber and can&rsquo;t be played or
        read from this dashboard. To assist a subscriber with a message, use portal view-as (audited).
      </div>

      {vmQ.isLoading ? (
        <div className="rounded-lg border border-gray-200 bg-white p-6 text-center text-sm text-gray-500">
          Loading voicemail…
        </div>
      ) : vmQ.isError ? (
        <div className="rounded-lg border border-gray-200 bg-white p-6 text-center text-sm text-gray-500">
          Couldn&rsquo;t load voicemail metadata.
        </div>
      ) : boxes.length === 0 && unlinked.length === 0 ? (
        <div className="rounded-lg border border-gray-200 bg-white p-6 text-center text-sm text-gray-500">
          No extensions — add one to get a voicemail box.
        </div>
      ) : (
        <div className="space-y-4">
          {boxes.map((box) => (
            <VoicemailBox
              key={`box:${box.sip_account_id}`}
              box={box}
              subscriberId={subscriberId}
              agentId={agentOf[box.sip_server_id ?? serverOfAccount[box.sip_account_id]] || null}
              pbxEnabled={vmEntryFor(box.sip_account_id)?.enabled ?? null}
              canWrite={canWrite}
              showConfigControls={mode === 'freepbx'}
              cloudMode={mode === 'cloud'}
              transcriptionAllowed={transcriptionAllowed}
              tierName={tierName}
              onSettingsSaved={() => queryClient.invalidateQueries({ queryKey: ['subscriber', String(subscriberId), 'voicemails'] })}
            />
          ))}
          {unlinked.map((box) => (
            <VoicemailBox
              key={`unlinked:${box.mailbox}`}
              box={box}
              agentId={null}
              canWrite={false}
              showConfigControls={false}
              cloudMode={false}
            />
          ))}
        </div>
      )}
    </div>
  );
}
